
import { sendStandardReturnInquiry } from "@dana/Services/DanaStandardReturnOrderService";
import { getSalseRepContacts } from "@dana/Services/StandardReturnStatusService";

import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import showContactModal from "@dana/Store/Pages/AnnualReturnsOrder/Handlers/ShowContactModal";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import {
    LoadingOverlay,
    Modal,
    TextArea,
} from "@insite/mobius/index";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface StandardReturnsContactModalStyle {
    modalWrapper?: InjectableCss;
}

export const standardReturnsContactModalStyle: StandardReturnsContactModalStyle = {
    modalWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 20px;
        `,
    },
};

const styles = standardReturnsContactModalStyle;


const mapStateToProps = (state: ApplicationStateDana) => ({
    isModalOpen: state.annualReturnsOrderState.showContactModal,
    session: getSession(state),
});

const mapDispatchToProps = {
    showContactModal,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;
const StandardReturnsContactModal: React.FC<Props> = ({ isModalOpen, session, showContactModal }) => {

    const [isModalLoading, setIsModalLoading] = useState(false);
    const [recipients, setRecipients] = useState("");
    const [recipientError, setRecipientError] = useState("");
    const [emailMessage, setEmailMessage] = useState("");
    const [sendCopy, setSendCopy] = useState(false);
    const [emailMessageError, setEmailMessageError] = useState("");

    const toasterContext = useContext(ToasterContext);

    const recipientChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRecipients(event.currentTarget.value);
        setRecipientError(event.currentTarget.value ? "" : translate("Recipient is required"));
    };

    const emailMessageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEmailMessage(event.currentTarget.value);
        setEmailMessageError(event.currentTarget.value ? "" : translate("Message is required"));
    };

    const sendCopyChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setSendCopy(value);
    };

    const validateRecipients = (value: string): string[] => {
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        return value
            .split(",")
            .map(s => s.trim())
            .filter(Boolean)
            .filter(email => !emailRegex.test(email));
    };

    const sendClickHandler = async () => {
        const invalids = validateRecipients(recipients);
        if (invalids.length > 0) {
            setRecipientError(translate("Invalid email address(es): {0}", invalids.join(", ")));
            return;
        }

        setIsModalLoading(true);

        const response = await sendStandardReturnInquiry({
            userName: session?.userName || "",
            message: emailMessage,
            recipients: recipients.split(",").map(s => s.trim()).filter(Boolean),
            ccRecipient: sendCopy && session?.email ? session.email : undefined,

        });
        if (response) {
            toasterContext.addToast({
                body: translate("Your email has been sent."),
                messageType: "success",
            });
        } else {
            toasterContext.addToast({
                body: translate("There was an error sending your email. Please try again later."),
                messageType: "danger",
            });
        }

        setIsModalLoading(false);
        showContactModal({ showContactModal: false });
    };

    const modalCloseHandler = () => {
        showContactModal({ showContactModal: false });
    };

    const populateRecipients = async () => {
        if (!session?.billToId) {
            return;
        }

        const contacts = await getSalseRepContacts(session.billToId);
        setRecipients(contacts.join(", "));
    };

    useEffect(() => {
        setIsModalLoading(true);

        populateRecipients();

        setIsModalLoading(false);
    }, []);

    return (
        <Modal
            isOpen={isModalOpen}
            handleClose={modalCloseHandler}
            headline={translate("Contact Sales Representative via Email")}
        >
            <LoadingOverlay loading={isModalLoading}>
                <StyledWrapper {...styles.modalWrapper}>
                    <TextField
                        label={translate("To :")}
                        value={recipients}
                        error={recipientError}
                        onChange={recipientChangeHandler}
                        required
                        placeholder={translate("Type in email addresses separated by commas.")}
                        data-test-selector="recipient"
                    />
                    <TextArea
                        label={translate("Message :")}
                        value={emailMessage}
                        error={emailMessageError}
                        required
                        onChange={emailMessageChangeHandler}
                        rows={10}
                        placeholder={translate("Type your message here...")}
                        data-test-selector="emailMessage"
                    />
                    <StyledWrapper>
                        <Checkbox
                            checked={sendCopy}
                            onChange={sendCopyChangeHandler}
                            data-test-selector="sendCopy"
                        >
                            {translate("Send me a copy of this email")}
                        </Checkbox>
                    </StyledWrapper>
                    <Button
                        variant="primary"
                        onClick={sendClickHandler}
                        disabled={!recipients || !emailMessage}
                        data-test-selector="sendContactEmailButton"
                    >
                        {translate("Send Email")}
                    </Button>

                </StyledWrapper>
            </LoadingOverlay>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(StandardReturnsContactModal);
